import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  PanelMode,
  loadPanelMode,
  nextPanelMode,
  panelIsOpen,
  panelModeLabel,
  panelStripLabel,
  panelStripWidth,
  panelTakesSpace,
  revealAfterModeChange,
  savePanelMode,
} from './panelMode';
import {
  NavSectionState,
  activeNavPath,
  loadNavSections,
  navSectionIsOpen,
  saveNavSections,
  sectionHasActive,
  toggleNavSection,
} from './navSections';
import { useAppStore } from '../state/store';
import { useViewport } from '../hooks/useViewport';
import { GlobalSearch } from './GlobalSearch';
import { HelpSidebar } from './HelpSidebar';
import { NotificationBell } from './NotificationBell';
import { OrgSwitcher } from './OrgSwitcher';
import { UserMenu } from './UserMenu';
import { navSections } from '../pages';
import { useFeature } from '../hooks/useFeature';
import { useHelpShortcut } from '../hooks/useHelpShortcut';
import { useProjectShell, visibleSections } from './ProjectLayoutState';
import { RailShell } from './ProjectLayoutRail';
import { COMPACT_LAYOUT_FEATURE } from '../features';
import { PhoneTabBar } from './ProjectLayoutTabs';
import { PhoneHeader, phonePageLabel } from './ProjectLayoutPhoneHeader';
import { PhoneMoreMenu } from './ProjectLayoutMore';

// ProjectLayout syncs the URL param into the store and renders the app
// shell (left nav + top bar) around the active module.
export const ProjectLayout: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Read the whole gate map rather than one key: the menu filters on
  // whatever a nav entry declares, so a future gated page needs no change
  // here.
  const features = useAppStore((s) => s.features);
  const { project, hasGuidedSession } = useProjectShell(projectId);
  const sections = visibleSections(navSections, hasGuidedSession, features?.features);
  const compactLayout = useFeature(COMPACT_LAYOUT_FEATURE);
  // How much room the project menu takes: pinned, auto-hiding on hover, or
  // hidden. Remembered per person so the choice survives a reload.
  const [navMode, setNavMode] = useState<PanelMode>(() => loadPanelMode('project-nav'));
  const [navHovered, setNavHovered] = useState(false);
  // An explicit "show it to me" from the edge strip. Hover alone never opens a
  // hidden menu, so this is the only way back from that mode, and it stays
  // open until dismissed rather than vanishing when the pointer moves.
  const [navRevealed, setNavRevealed] = useState(false);
  // Which menu groups are expanded. They start collapsed — see navSections.ts.
  const [navOpenSections, setNavOpenSections] = useState<NavSectionState>(() => loadNavSections());
  // Phones and tablets get a top bar with a menu button and the nav as a
  // drawer over the page; the pinned / auto-hide / hidden choice is a desktop
  // preference and is left untouched for when the window is wide again.
  const viewport = useViewport();
  const compact = viewport.isCompact;
  // In the compact layout a phone's menu is the More page, not the drawer.
  const phoneMore = compactLayout && compact;
  // On phones the help panel opens from the top bar rather than a floating
  // button, which sat on top of every composer at the bottom of the page.
  const [helpOpen, setHelpOpen] = useState(false);

  // Escape dismisses a revealed menu, the same key that closes every other
  // thing this app floats over the document.
  useEffect(() => {
    if (!navRevealed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavRevealed(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navRevealed]);

  // Navigating from the menu is the commonest reason to be done with it.
  useEffect(() => {
    setNavRevealed(false);
  }, [location.pathname]);

  const activePath = activeNavPath(location.pathname, projectId || '');
  // A touch screen cannot hover, so auto-hide behaves as hidden there: the
  // edge strip (wider, to be tappable) reveals the menu on tap instead.
  const open = compact
    ? navRevealed
    : panelIsOpen(navMode, navHovered && !viewport.coarsePointer, navRevealed);
  const takesSpace = compact ? false : panelTakesSpace(navMode);
  const stripWidth = panelStripWidth(viewport.coarsePointer);
  useHelpShortcut(compactLayout, () => setHelpOpen((o) => !o));
  const help = <HelpSidebar open={helpOpen} onOpenChange={setHelpOpen} />;

  if (compactLayout && !compact) {
    return (
      <RailShell
        project={project}
        projectId={projectId || ''}
        sections={sections}
        helpOpen={helpOpen}
        onHelpToggle={() => setHelpOpen((o) => !o)}
        help={help}
      />
    );
  }

  return (
    <div
      className="app-shell"
      style={{ display: 'flex', flexDirection: compact ? 'column' : 'row', overflow: 'hidden' }}
    >
      {compact && (
        <PhoneHeader
          project={project}
          pageLabel={phonePageLabel(sections, activePath)}
          menuOpen={navRevealed}
          onMenuToggle={() => setNavRevealed((shown) => !shown)}
          helpOpen={helpOpen}
          onHelpToggle={() => setHelpOpen((o) => !o)}
        />
      )}
      {/* The edge strip is what brings a hidden or auto-hiding nav back: with
          the nav gone there would otherwise be nothing left to click. */}
      {!compact && !takesSpace && (
        <button
          type="button"
          className="panel-edge-strip"
          aria-label={panelStripLabel('the project menu', open)}
          aria-expanded={open}
          onMouseEnter={() => !viewport.coarsePointer && setNavHovered(true)}
          onMouseLeave={() => setNavHovered(false)}
          style={{
            width: stripWidth,
            minWidth: stripWidth,
            background: 'var(--sidebar-bg)',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: 12,
            color: 'var(--sidebar-text-dim)',
          }}
          title={`${panelStripLabel('the project menu', open)} (${panelModeLabel(
            navMode
          )}) — the button inside the menu changes the mode`}
          onClick={() => setNavRevealed((shown) => !shown)}
        >
          <span className="panel-edge-strip-knob" aria-hidden="true">
            {open ? '\u2039' : '\u203a'}
          </span>
        </button>
      )}
      {/* Clicking away closes a menu that was revealed on purpose: an overlay
          with no way out but the edge strip is a trap. */}
      {navRevealed && !takesSpace && (
        <div
          onClick={() => setNavRevealed(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 899 }}
        />
      )}
      <aside
        aria-label="Project navigation"
        onMouseEnter={() => navMode === 'autohide' && !compact && setNavHovered(true)}
        onMouseLeave={() => navMode === 'autohide' && !compact && setNavHovered(false)}
        style={{
          width: open ? (phoneMore ? '100vw' : compact ? 'min(300px, 85vw)' : 200) : 0,
          minWidth: open ? (phoneMore ? '100vw' : compact ? 'min(300px, 85vw)' : 200) : 0,
          background: phoneMore ? 'var(--bg-app)' : 'var(--sidebar-bg)',
          color: phoneMore ? 'var(--text)' : 'var(--sidebar-text)',
          display: open ? 'flex' : 'none',
          flexDirection: 'column',
          // An auto-hiding nav floats over the document instead of reflowing
          // it every time the pointer crosses the edge; on a phone the drawer
          // always floats.
          ...(takesSpace
            ? {}
            : {
                position: 'fixed',
                left: compact ? 0 : stripWidth,
                top: 0,
                bottom: 0,
                zIndex: 900,
                boxShadow: '2px 0 8px rgba(0,0,0,0.25)',
              }),
        }}
      >
        {phoneMore ? (
          <PhoneMoreMenu
            project={project}
            sections={sections}
            activePath={activePath}
            openSections={navOpenSections}
            setOpenSections={setNavOpenSections}
            onClose={() => setNavRevealed(false)}
          />
        ) : (
        <>
        <div style={{ padding: '16px 14px', borderBottom: '1px solid var(--sidebar-border)', flexShrink: 0 }}>
          {compact && (
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setNavRevealed(false)}
              style={{
                float: 'right',
                width: 44,
                height: 44,
                margin: '-10px -10px 0 0',
                background: 'none',
                border: 'none',
                color: 'var(--sidebar-text-dim)',
                fontSize: 20,
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          )}
          <OrgSwitcher variant="dark" />
          {/* Explicit way back to the workspace's project list — the project
              name alone read as a label, not a link. */}
          <div
            style={{
              fontSize: 12,
              color: 'var(--sidebar-text-dim)',
              marginTop: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
            title="Back to all projects in this workspace"
            onClick={() => navigate('/projects')}
          >
            <span aria-hidden>←</span> All projects
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--sidebar-text)',
              marginTop: 6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={project?.name || ''}
          >
            {project?.name || '…'}
          </div>
          <div style={{ marginTop: 8 }}>
            <GlobalSearch />
          </div>
        </div>
        {/* minHeight: 0 is what lets this shrink below its content in the
            column, so a menu taller than the window scrolls here instead of
            pushing the account controls off the bottom. */}
        <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 0' }} data-help-key="start.navigation">
          {sections.map((section, i) => {
            const items = section.items;
            // An unlabelled group has no header to click, so it never collapses.
            const label = section.label;
            const expanded = label
              ? navSectionIsOpen(navOpenSections, label, sectionHasActive(items, activePath))
              : true;
            return (
              <div key={label || `section-${i}`} style={{ marginTop: i === 0 ? 0 : 10 }}>
                {label && (
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => {
                      const next = toggleNavSection(navOpenSections, label, expanded);
                      setNavOpenSections(next);
                      saveNavSections(next);
                    }}
                    title={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      width: '100%',
                      padding: '6px 16px',
                      minHeight: compact ? 36 : 28,
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      color: 'var(--sidebar-text-faint)',
                      cursor: 'pointer',
                    }}
                  >
                    <span aria-hidden style={{ fontSize: 12, width: 12 }}>
                      {expanded ? '▾' : '▸'}
                    </span>
                    {label}
                  </button>
                )}
                {expanded &&
                  items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      style={({ isActive }) => ({
                        display: 'block',
                        padding: compact ? '12px 16px' : '9px 16px',
                        color: isActive ? 'var(--accent-fg)' : 'var(--sidebar-text-dim)',
                        background: isActive ? 'var(--accent)' : 'transparent',
                        textDecoration: 'none',
                        fontSize: 14,
                      })}
                    >
                      {item.label}
                    </NavLink>
                  ))}
              </div>
            );
          })}
          <div style={{ borderTop: '1px solid var(--sidebar-border)', margin: '8px 0' }} />
          {/* Plain anchor (not NavLink): the manual opens in a new tab so it
              doesn't navigate the user away from their work (issue #162). */}
          <a
            href="/manual"
            target="_blank"
            rel="noopener"
            title="Open the user manual in a new tab"
            style={{
              display: 'block',
              padding: '9px 16px',
              color: 'var(--sidebar-text-dim)',
              textDecoration: 'none',
              fontSize: 14,
            }}
          >
            Help
          </a>
        </nav>
        <div style={{ borderTop: '1px solid var(--sidebar-border)', padding: 12, flexShrink: 0 }}>
          {/* The bell lives with the account controls: both are about the
              person using OpenV rather than the project they are in. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <UserMenu variant="dark" />
            </div>
            {/* The ? sits with the bell for the same reason the bell sits
                with the account controls: both are about the person, not the
                project. It used to float over the bottom-right corner of the
                page, where it covered whatever the page put there. */}
            {!compact && (
              <button
                type="button"
                aria-label="Help"
                aria-expanded={helpOpen}
                data-help-ui=""
                onClick={() => setHelpOpen((o) => !o)}
                title="Help for this page"
                style={{
                  width: 32,
                  height: 32,
                  background: 'none',
                  border: 'none',
                  color: 'var(--sidebar-text)',
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                ?
              </button>
            )}
            <NotificationBell variant="dark" />
          </div>
          {!compact && (
          <button
            onClick={() => {
              const next = nextPanelMode(navMode);
              setNavMode(next);
              savePanelMode('project-nav', next);
              setNavHovered(false);
              // Keep the menu on screen while its mode is being chosen, so
              // this button does not vanish mid-cycle (issue #362).
              setNavRevealed(revealAfterModeChange(next));
            }}
            title={`Project menu: ${panelModeLabel(navMode)} — click for ${panelModeLabel(
              nextPanelMode(navMode)
            )}`}
            style={{
              marginTop: 8,
              width: '100%',
              background: 'none',
              border: '1px solid var(--sidebar-border)',
              borderRadius: 4,
              color: 'var(--sidebar-text-dim)',
              fontSize: 12,
              padding: '5px 6px',
              minHeight: 26,
              cursor: 'pointer',
            }}
          >
            Menu: {panelModeLabel(navMode)}
          </button>
          )}
        </div>
        </>
        )}
      </aside>
      <main style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto', background: 'var(--bg-app)' }}>
        <Outlet />
      </main>
      {compactLayout && compact && (
        <PhoneTabBar menuOpen={navRevealed} onMore={() => setNavRevealed((shown) => !shown)} />
      )}
      {/* Context-aware help — mounted once here so it is available on every
          project page (issue #162). Controlled from whichever bar this shell
          is showing: the compact top bar, or the sidebar's account row. */}
      {help}
    </div>
  );
};
